<div class="page">
  <div class="header">
    <h1 class="title">Benefits</h1>
    <div class="subtitle">Manage and assign benefits and snacks to your teams</div>
  </div>

  <section class="page-loading" *ngIf="!isInitialCompanyCheckComplete">
    <mat-spinner [diameter]="32"></mat-spinner>
    <span>Loading</span>
  </section>

  <section class="setup-cta" *ngIf="isInitialCompanyCheckComplete && !hasCompany && !isNativeCompanyManager">
    <div class="setup-cta-content">
      <mat-icon>business</mat-icon>
      <div class="setup-cta-copy">
        <h2>Create your first company</h2>
        <p>To use Benefits, create a company first in Company > Company.</p>
      </div>
    </div>
    <button mat-raised-button color="primary" type="button" (click)="onGoToCompanyTab()">
      Go to Company
    </button>
  </section>

  <ng-container *ngIf="isInitialCompanyCheckComplete && hasCompany">
    <div class="top-tabs">
      <button type="button" class="top-tab" [class.active]="activeTopTab === 'benefits'" (click)="setTopTab('benefits')">
        <mat-icon>favorite_border</mat-icon>
        Assign Benefits
      </button>
      <button
        *ngIf="showSnacksSection"
        type="button"
        class="top-tab"
        [class.active]="activeTopTab === 'snacks'"
        (click)="setTopTab('snacks')"
      >
        <mat-icon>campaign</mat-icon>
        Assign Snacks
      </button>
      <button
        *ngIf="!isNativeCompanyManager"
        type="button"
        class="top-tab"
        [class.active]="activeTopTab === 'contributions'"
        (click)="setTopTab('contributions')"
      >
        <mat-icon>attach_money</mat-icon>
        Assign Contributions
      </button>
    </div>

    <ng-container [ngSwitch]="activeTopTab">
    <ng-container *ngSwitchCase="'benefits'">
      <div class="summary" *ngIf="!isLoading">
        <div class="step">
          <span class="step-index">1</span>
          <span class="step-text">Choose which benefits to assign to teams</span>
          <span class="badge">{{ selectedBenefitsCount }} selected</span>
        </div>

        <div class="step">
          <span class="step-index">2</span>
          <span class="step-text">Choose which teams to assign the benefits to</span>
          <span class="badge">{{ selectedTeamsCount }} team<span *ngIf="selectedTeamsCount !== 1">s</span> selected</span>
        </div>

        <div class="summary-actions">
          <button
            mat-stroked-button
            type="button"
            [disabled]="isSaving || !hasSelections"
            (click)="clearSelections()"
          >
            Clear Selections
          </button>

          <button mat-raised-button color="primary" [disabled]="!canAssign" (click)="assignBenefitsToTeams()">
            Assign Benefits ({{ selectedBenefitsCount }} to {{ selectedTeamsCount }} team<span *ngIf="selectedTeamsCount !== 1">s</span>)
          </button>
        </div>
      </div>

      <div class="loading" *ngIf="isLoading">Loading benefits and teams...</div>

      <div class="grid" *ngIf="!isLoading">
        <section class="panel benefits-panel">
          <div class="panel-header">
            <h2>Step 1: Benefits</h2>
            <mat-checkbox
              [checked]="isAllBenefitsSelected"
              [indeterminate]="isSomeBenefitsSelected"
              (change)="onToggleAllBenefits($event)"
              [disabled]="isSaving || !benefitProducts.length"
            >
              Select all
            </mat-checkbox>
          </div>

          <div class="categories" *ngIf="benefitProducts.length; else noBenefits">
            <ng-container *ngFor="let category of categories; trackBy: trackCategory">
              <ng-container *ngIf="getPopulatedBenefitSections(category.id) as sections">
                <div class="category" *ngIf="sections.length > 0">
                  <h3>{{ category.title }}</h3>

                  <div class="section" *ngFor="let section of sections; trackBy: trackSection">
                    <div class="section-title">{{ section.title }}</div>

                    <div class="product-list">
                      <div
                        class="product"
                        *ngFor="let product of section.products; trackBy: trackProduct"
                        [class.product-disabled]="isBenefitProductPending(product)"
                        [class.product-requestable]="isBenefitProductRequestable(product)"
                      >
                        <div
                          class="product-header"
                          (click)="onBenefitProductClicked(product)"
                        >
                          <mat-checkbox
                            [checked]="isBenefitProductSelected(product.id)"
                            [disabled]="!canInteractWithBenefitProductCheckbox(product)"
                            (click)="$event.stopPropagation()"
                            (change)="onBenefitProductCheckboxChanged(product, $event.checked)">
                          </mat-checkbox>

                          <div class="logo-wrap">
                            <img
                              *ngIf="shouldShowBenefitProductImage(product)"
                              [src]="product.imageUrl"
                              [alt]="(product.vendorName || product.productName || 'Benefit') + ' logo'"
                              class="logo"
                              loading="eager"
                              decoding="async"
                              referrerpolicy="no-referrer"
                              (load)="onBenefitProductImageLoaded(product)"
                              (error)="onBenefitProductImageError(product)"
                            />
                            <div *ngIf="shouldShowBenefitProductImagePlaceholder(product)" class="logo-placeholder">
                              {{ (product.vendorName || product.productName || '?').charAt(0) }}
                            </div>
                          </div>

                          <div class="product-copy">
                            <div class="name">{{ product.vendorName || product.productName }}</div>
                            <div class="description">{{ product.productName }}</div>
                            <div class="group-onboarding-label status-available" *ngIf="product.alwaysActive">
                              Always available for all teams
                            </div>
                            <div class="group-onboarding-label" [ngClass]="getBenefitProductStatusClass(product)" *ngIf="getBenefitProductStatusLabel(product)">
                              {{ getBenefitProductStatusLabel(product) }}
                            </div>
                            <div class="group-onboarding-reason" *ngIf="product.approvalDisabledReason">
                              {{ product.approvalDisabledReason }}
                            </div>
                          </div>

                          <div class="product-actions" *ngIf="product.detailsUrl">
                            <button
                              mat-stroked-button
                              type="button"
                              class="product-details-btn"
                              (click)="openBenefitProductDetails(product, $event)"
                            >
                              View details
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ng-container>
            </ng-container>
          </div>

          <ng-template #noBenefits>
            <div class="empty">No benefits are configured for this company yet.</div>
          </ng-template>
        </section>

        <section class="panel teams-panel">
          <div class="panel-header">
            <h2>Step 2: Teams</h2>
          </div>

          <div class="team-table" *ngIf="teams.length; else noTeams">
            <div class="team-row head">
              <div class="col-select">
                <mat-checkbox
                  [checked]="isAllTeamsSelected"
                  [indeterminate]="isSomeTeamsSelected"
                  (change)="onToggleAllTeams($event)"
                  [disabled]="isSaving"
                >
                </mat-checkbox>
              </div>
              <div class="col-name">Team Name</div>
              <div class="col-members">Members</div>
              <div class="col-action"></div>
            </div>

            <div class="team-row"
                 *ngFor="let team of teams; trackBy: trackTeam"
                 [class.selected]="isTeamSelected(team.id)"
                 (click)="toggleTeam(team.id)">
              <div class="col-select">
                <mat-checkbox
                  [checked]="isTeamSelected(team.id)"
                  [disabled]="isSaving"
                  (click)="$event.stopPropagation()"
                  (change)="onTeamToggle(team.id, $event.checked)">
                </mat-checkbox>
              </div>
              <div class="col-name">{{ team.name }}</div>
              <div class="col-members">{{ team.membersCount }}</div>
              <div class="col-action">
                <button
                  mat-stroked-button
                  type="button"
                  [disabled]="isSaving"
                  (click)="$event.stopPropagation(); loadBenefitSelectionsFromTeam(team)"
                >
                  Load Benefits
                </button>
              </div>
            </div>
          </div>

          <ng-template #noTeams>
            <div class="empty">No teams found for this company.</div>
          </ng-template>
        </section>
      </div>
    </ng-container>

    <ng-container *ngSwitchCase="'snacks'">
      <div class="summary" *ngIf="!isLoading && !isLoadingSnackRewards">
        <div class="step">
          <span class="step-index">1</span>
          <span class="step-text">Choose which company-available snacks to enable for teams</span>
          <span class="badge">{{ selectedSnacksCount }} selected</span>
        </div>

        <div class="step">
          <span class="step-index">2</span>
          <span class="step-text">Choose which teams to assign the snacks to</span>
          <span class="badge">{{ selectedTeamsCount }} team<span *ngIf="selectedTeamsCount !== 1">s</span> selected</span>
        </div>

        <div class="summary-actions">
          <button
            mat-stroked-button
            type="button"
            [disabled]="isSaving || (!selectedSnacksCount && !selectedTeamsCount)"
            (click)="clearSnackSelections()"
          >
            Clear Selections
          </button>

          <button mat-raised-button color="primary" [disabled]="!canAssignSnacks" (click)="assignSnacksToTeams()">
            Assign Snacks ({{ selectedSnacksCount }} to {{ selectedTeamsCount }} team<span *ngIf="selectedTeamsCount !== 1">s</span>)
          </button>
        </div>
      </div>

      <div class="loading" *ngIf="isLoading || isLoadingSnackRewards">Loading snacks and teams...</div>

      <div class="grid" *ngIf="!isLoading && !isLoadingSnackRewards">
        <section class="panel benefits-panel">
          <div class="panel-header">
            <h2>Step 1: Snacks</h2>
            <mat-checkbox
              [checked]="isAllSnacksSelected"
              [indeterminate]="isSomeSnacksSelected"
              (change)="onToggleAllSnacks($event)"
              [disabled]="isSaving || !snackRewards.length"
            >
              Select all
            </mat-checkbox>
          </div>

          <div class="categories" *ngIf="snackRewards.length; else noSnacks">
            <div class="category">
              <div class="product-list">
                <div
                  class="product"
                  *ngFor="let reward of snackRewards; trackBy: trackSnackReward"
                >
                  <div class="product-header" (click)="toggleSnackReward(reward.id)">
                    <mat-checkbox
                      [checked]="isSnackRewardSelected(reward.id)"
                      [disabled]="isSaving"
                      (click)="$event.stopPropagation()"
                      (change)="onSnackRewardToggle(reward.id, $event.checked)">
                    </mat-checkbox>

                    <div class="logo-wrap">
                      <img
                        *ngIf="reward.imageUrl"
                        [src]="reward.imageUrl"
                        [alt]="reward.title + ' image'"
                        class="logo"
                        loading="eager"
                        decoding="async"
                        referrerpolicy="no-referrer"
                      />
                      <div *ngIf="!reward.imageUrl" class="logo-placeholder">
                        {{ (reward.title || '?').charAt(0) }}
                      </div>
                    </div>

                    <div class="product-copy">
                      <div class="name">{{ reward.title }}</div>
                      <div class="description" *ngIf="reward.subtitle">{{ reward.subtitle }}</div>
                    </div>

                    <button
                      mat-icon-button
                      type="button"
                      matTooltip="Preview Explore panel"
                      [class.active-preview-button]="isSnackRewardPreviewed(reward.id)"
                      (click)="previewSnackReward(reward, $event)">
                      <mat-icon>visibility</mat-icon>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="snack-preview-inline">
              <app-snack-reward-preview [reward]="getPreviewSnackReward()"></app-snack-reward-preview>
            </div>
          </div>

          <ng-template #noSnacks>
            <div class="empty">No snack rewards are available for this company.</div>
          </ng-template>
        </section>

        <section class="panel teams-panel">
          <div class="panel-header">
            <h2>Step 2: Teams</h2>
          </div>

          <div class="team-table" *ngIf="teams.length; else noSnackTeams">
            <div class="team-row head">
              <div class="col-select">
                <mat-checkbox
                  [checked]="isAllTeamsSelected"
                  [indeterminate]="isSomeTeamsSelected"
                  (change)="onToggleAllTeams($event)"
                  [disabled]="isSaving"
                >
                </mat-checkbox>
              </div>
              <div class="col-name">Team Name</div>
              <div class="col-members">Members</div>
              <div class="col-action"></div>
            </div>

            <div class="team-row"
                 *ngFor="let team of teams; trackBy: trackTeam"
                 [class.selected]="isTeamSelected(team.id)"
                 (click)="toggleTeam(team.id)">
              <div class="col-select">
                <mat-checkbox
                  [checked]="isTeamSelected(team.id)"
                  [disabled]="isSaving"
                  (click)="$event.stopPropagation()"
                  (change)="onTeamToggle(team.id, $event.checked)">
                </mat-checkbox>
              </div>
              <div class="col-name">{{ team.name }}</div>
              <div class="col-members">{{ team.membersCount }}</div>
              <div class="col-action">
                <button
                  mat-stroked-button
                  type="button"
                  [disabled]="isSaving"
                  (click)="$event.stopPropagation(); loadSnackSelectionsFromTeam(team)"
                >
                  Load Snacks
                </button>
              </div>
            </div>
          </div>

          <ng-template #noSnackTeams>
            <div class="empty">No teams found for this company.</div>
          </ng-template>
        </section>
      </div>
    </ng-container>

    <ng-container *ngSwitchDefault>
      <section class="contributions-upsell">
        <app-coming-soon-upsell
          featureKey="payroll-assign-contributions"
          title="Assign Contributions is coming soon"
          description="We are actively building contribution assignment tools so you can manage employer and employee contribution settings by team."
          [renderMode]="'expanded'"
          [allowFeedback]="true"
          [context]="{ source: 'payroll-benefits-page', tab: 'assign-contributions' }"
        >
        </app-coming-soon-upsell>
      </section>
    </ng-container>
    </ng-container>
  </ng-container>
</div>
